/**
 * Phase handlers for the company stage strategy
 */

import { StrategyPhase } from '../baseStrategy';
import { generateCompanyStageData } from './utils';
import { CompanyStageOptions } from './components';
import { runAIMatches, runMultiMatches } from '../../api';

/**
 * Handler for the init phase of the company stage strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const initPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Company stage strategy: init phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nInitializing company stage discovery...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Make sure matchResults is passed to the decision object
    const updatedDecision = {
      ...decision,
      // If handleRefreshResults is available, use it to get the latest matchResults
      handleRefreshResults: decision.handleRefreshResults
    };

    // Generate company stage data
    const { stageData, aiMatchesResult } = 
      await generateCompanyStageData(messages, setMessages, updatedDecision, true);

    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nCompany stage data generated successfully.`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    // Return a decision that will render the company stage options component
    return {
      ...decision,
      actionType: 'RENDER_COMPANY_STAGE_OPTIONS',
      component: CompanyStageOptions,
      strategyPhase: StrategyPhase.SUGGESTIONS,
      aiMatchesResult,
      componentProps: {
        stageData: {
          ...stageData,
          totalCount: aiMatchesResult?.companyStageTotal || 0
        },
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        onStagesSelected: stages => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedCompanyStages: stages
          };
        }
      }
    };
  } catch (error) {
    console.error('Error generating company stage options:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.SUGGESTIONS
    };
  }
};

/**
 * Handler for the suggestions phase of the company stage strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const suggestionsPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Company stage strategy: suggestions phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating company stage suggestions...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Generate company stage data
    const { stageData, aiMatchesResult } = 
      await generateCompanyStageData(messages, setMessages, decision, true);
    
    // Return a decision that will render the company stage options component
    return {
      ...decision,
      actionType: 'RENDER_COMPANY_STAGE_OPTIONS',
      component: CompanyStageOptions,
      strategyPhase: StrategyPhase.CONVERSATION,
      aiMatchesResult,
      componentProps: {
        stageData: {
          ...stageData,
          totalCount: aiMatchesResult?.companyStageTotal || 0
        },
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        onStagesSelected: stages => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedCompanyStages: stages
          };
        }
      }
    };
  } catch (error) {
    console.error('Error generating company stage suggestions:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for the conversation phase of the company stage strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const conversationPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Company stage strategy: conversation phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nContinuing conversation about company stages...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Generate company stage data
    const { stageData, aiMatchesResult } = 
      await generateCompanyStageData(messages, setMessages, decision, true);
    
    // Return a decision that will render the company stage options component
    return {
      ...decision,
      actionType: 'RENDER_COMPANY_STAGE_OPTIONS',
      component: CompanyStageOptions,
      strategyPhase: StrategyPhase.CONVERSATION,
      aiMatchesResult,
      componentProps: {
        stageData: {
          ...stageData,
          totalCount: aiMatchesResult?.companyStageTotal || 0
        },
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        onStagesSelected: stages => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedCompanyStages: stages
          };
        }
      }
    };
  } catch (error) {
    console.error('Error in conversation phase:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for rendering the company stage options component
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const renderComponentHandler = async (decision, messages, setMessages) => {
  console.log('Company stage strategy: render component');
  
  // If we already have the component and stage data, just return the decision
  if (decision.component === CompanyStageOptions && decision.componentProps?.stageData) {
    return decision;
  }
  
  // Otherwise, generate the stage data
  try {
    // Generate company stage data
    const { stageData, aiMatchesResult } = 
      await generateCompanyStageData(messages, setMessages, decision, true);
    
    // Return a decision that will render the company stage options component
    return {
      ...decision,
      actionType: 'RENDER_COMPANY_STAGE_OPTIONS',
      component: CompanyStageOptions,
      aiMatchesResult,
      componentProps: {
        stageData: {
          ...stageData,
          totalCount: aiMatchesResult?.companyStageTotal || 0
        },
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        onStagesSelected: stages => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedCompanyStages: stages
          };
        }
      }
    };
  } catch (error) {
    console.error('Error generating company stage options for component:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};
